import React, { useState } from 'react';
import { Github, Linkedin, Mail, Phone, ExternalLink, Database, Code, BarChart3, Brain, Cloud, Shield, Smartphone, Briefcase, Award, TrendingUp, Users, MessageSquare, Building2, Music, Activity, Lock } from 'lucide-react';

const LightningBackground = () => {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900">
      <div className="lightning-container">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="lightning"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>
      <style>{`
        .lightning-container { position: absolute; width: 100%; height: 100%; }
        .lightning {
          position: absolute; width: 2px; height: 100%;
          background: linear-gradient(180deg, transparent 0%, rgba(147, 51, 234, 0.8) 20%, rgba(59, 130, 246, 0.9) 50%, rgba(147, 51, 234, 0.8) 80%, transparent 100%);
          filter: blur(2px); animation: lightning-strike linear infinite; opacity: 0;
        }
        @keyframes lightning-strike {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          10% { opacity: 1; } 15% { opacity: 0.3; } 20% { opacity: 1; }
          50% { opacity: 0; transform: translateY(100%); }
        }
      `}</style>
    </div>
  );
};

const ProfileCard = ({ name, title, bio }) => {
  const [imageError, setImageError] = useState(false);
  
  return (
    <div className="profile-card group relative w-full max-w-2xl">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
      <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-2xl p-8 border border-purple-500/20 shadow-2xl">
        <div className="flex flex-col items-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full blur-md opacity-75"></div>
            <div className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-purple-500/50">
              {!imageError ? (
                <img 
                  src="/profile.jpg" 
                  alt="Sello Kgole"
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-6xl font-bold text-white">
                  SK
                </div>
              )}
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-2">{name}</h2>
          <p className="text-purple-400 text-xl mb-2 flex items-center gap-2">
            <Database size={20} />
            {title}
          </p>
          <p className="text-blue-300 text-sm mb-4">🎓 Second-year IT Computer Science student at Richfield College</p>
          <p className="text-slate-300 text-center mb-6 leading-relaxed max-w-xl">{bio}</p>
        </div>
      </div>
      <style>{`
        .profile-card { animation: float 6s ease-in-out infinite; }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
      `}</style>
    </div>
  );
};

const ProjectCard = ({ title, description, tech, github, icon: Icon, coverImage }) => {
  const [imgError, setImgError] = useState(false);
  
  return (
    <div className="group bg-slate-900/70 backdrop-blur-sm rounded-xl overflow-hidden border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:transform hover:scale-105">
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-purple-900 to-blue-900">
        {!imgError && coverImage ? (
          <img 
            src={coverImage} 
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="text-purple-400/50" size={80} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent opacity-60"></div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <Icon className="text-purple-400" size={24} />
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        <p className="text-slate-300 mb-4 text-sm leading-relaxed">{description}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {tech.map((t, i) => (
            <span key={i} className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-xs">
              {t}
            </span>
          ))}
        </div>
        <a href={github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors">
          <Github size={18} />
          <span className="text-sm">View on GitHub</span>
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
};

const SkillCategory = ({ icon: Icon, title, items }) => (
  <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
    <div className="flex items-center gap-3 mb-4">
      <Icon className="text-purple-400" size={24} />
      <h3 className="text-xl font-bold text-white">{title}</h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-slate-300 flex items-center gap-2 text-sm">
          <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const InterestCard = ({ icon: Icon, title, color }) => (
  <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-4 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:transform hover:scale-105">
    <div className="flex items-center gap-3">
      <Icon className={`${color} flex-shrink-0`} size={24} />
      <span className="text-slate-200 text-sm">{title}</span>
    </div>
  </div>
);

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');

  const projects = [
    {
      title: "Luna Data Science Chatbot",
      description: "An intelligent chatbot for data science tasks including data analysis, visualization, and machine learning. Built to assist data scientists with automated insights and interactive data exploration.",
      tech: ["Python", "NLP", "Machine Learning", "Data Analysis"],
      github: "https://github.com/sello-bot/luna-data-science-chatbot",
      icon: MessageSquare,
      coverImage: "/projects/luna-chatbot.jpg"
    },
    {
      title: "Bank Management System",
      description: "A console-based C++ application for managing banking operations. Uses Object-Oriented Programming principles, STL for data storage, and file I/O for persistent data management.",
      tech: ["C++", "OOP", "STL", "File I/O"],
      github: "https://github.com/sello-bot/BankManagementSystem",
      icon: Building2,
      coverImage: "/projects/bank-system.jpg"
    },
    {
      title: "Spotify Listening History Analysis",
      description: "Comprehensive analysis of Spotify listening history across countries. Explores user behavior patterns, track metadata, and playback trends spanning multiple years of data.",
      tech: ["Python", "Pandas", "Data Visualization", "Statistical Analysis"],
      github: "https://github.com/sello-bot/Top-Spotify-Listening-History-Songs-in-Countries",
      icon: Music,
      coverImage: "/projects/spotify-analysis.jpg"
    },
    {
      title: "Breast Cancer Prediction ML Model",
      description: "Machine learning project using the Wisconsin Diagnostic Dataset to predict whether breast cancer is benign or malignant. Implements various classification algorithms for accurate diagnosis.",
      tech: ["Python", "Scikit-learn", "Machine Learning", "Data Preprocessing"],
      github: "https://github.com/sello-bot/Machine-Learning-Project-Breast-Cancer-Wisconsin-Diagnostic-Data-Set",
      icon: Activity,
      coverImage: "/projects/breast-cancer-ml.jpg"
    },
    {
      title: "Global Cybersecurity Threats Analysis",
      description: "SQL-based analysis of cybersecurity incidents from 2015-2024. Tracks attack vectors, threat patterns, and security trends using comprehensive dataset querying and reporting.",
      tech: ["SQL", "Data Analysis", "Cybersecurity", "Database Management"],
      github: "https://github.com/sello-bot/-Global-Cybersecurity-Threats-2015-2024-SQL",
      icon: Lock,
      coverImage: "/projects/cybersecurity-threats.jpg"
    }
  ];

  const dataSkills = [
    "Python (Pandas, NumPy)",
    "SQL & Database Management",
    "Excel (Advanced)",
    "Machine Learning Algorithms",
    "Statistical Modeling",
    "Data Visualization",
    "Matplotlib & Seaborn",
    "Exploratory Data Analysis",
    "Predictive Modeling",
    "A/B Testing"
  ];

  const webSkills = [
    "HTML5 & CSS3",
    "JavaScript (ES6+)",
    "React.js",
    "Node.js",
    "Express.js",
    "Database Design",
    "RESTful APIs",
    "Responsive Design"
  ];

  const toolsSkills = [
    "VS Code",
    "Git & GitHub",
    "Microsoft Word",
    "Microsoft PowerPoint",
    "Microsoft Excel",
    "Version Control"
  ];

  const interests = [
    { icon: Brain, title: "Artificial Intelligence & Machine Learning", color: "text-purple-400" },
    { icon: BarChart3, title: "Data Science & Business Analytics", color: "text-blue-400" },
    { icon: Cloud, title: "Cloud Computing & Infrastructure", color: "text-cyan-400" },
    { icon: Shield, title: "Cybersecurity & Data Protection", color: "text-red-400" },
    { icon: Smartphone, title: "Full-Stack Web Development", color: "text-green-400" },
    { icon: Briefcase, title: "IT Project Management & Consulting", color: "text-yellow-400" }
  ];

  return (
    <div className="min-h-screen relative">
      <LightningBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/50 backdrop-blur-lg border-b border-purple-500/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-white">SK</h1>
            <div className="flex gap-6">
              {['home', 'about', 'projects', 'skills', 'interests', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`capitalize transition-colors ${
                    activeSection === section ? 'text-purple-400' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {section}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-6 pt-24 pb-12">
        {/* Home Section */}
        {activeSection === 'home' && (
          <section className="min-h-[calc(100vh-6rem)] flex flex-col items-center justify-center">
            <ProfileCard
              name="Sello Kgole"
              title="Data Scientist & IT Enthusiast"
              bio="Passionate about transforming raw data into actionable insights and building innovative solutions that drive informed decision-making."
            />
            <div className="flex gap-6 mt-8 flex-wrap justify-center">
              <a href="https://github.com/sello-bot" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-900/70 backdrop-blur-sm rounded-full border border-purple-500/20 hover:border-purple-500/50 transition-all hover:scale-110">
                <Github className="text-purple-400" size={24} />
              </a>
              <a href="https://www.linkedin.com/in/sello-kgole-ba450a295" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-900/70 backdrop-blur-sm rounded-full border border-purple-500/20 hover:border-purple-500/50 transition-all hover:scale-110">
                <Linkedin className="text-purple-400" size={24} />
              </a>
              <a href="mailto:skgole6@gmail.com" className="p-3 bg-slate-900/70 backdrop-blur-sm rounded-full border border-purple-500/20 hover:border-purple-500/50 transition-all hover:scale-110">
                <Mail className="text-purple-400" size={24} />
              </a>
              <a href="tel:0677267091" className="p-3 bg-slate-900/70 backdrop-blur-sm rounded-full border border-purple-500/20 hover:border-purple-500/50 transition-all hover:scale-110">
                <Phone className="text-purple-400" size={24} />
              </a>
            </div>
          </section>
        )}

        {/* About Section */}
        {activeSection === 'about' && (
          <section className="animate-fade-in max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">About Me</h2>
            <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-8 border border-purple-500/20 space-y-6">
              <div className="flex items-start gap-4">
                <BarChart3 className="text-purple-400 mt-1 flex-shrink-0" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">What I Do</h3>
                  <p className="text-slate-300 leading-relaxed">
                    I specialize in extracting meaningful insights from complex datasets using advanced analytical techniques and modern programming tools. My focus spans from building predictive models to creating interactive dashboards that help businesses make data-driven decisions.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Award className="text-blue-400 mt-1 flex-shrink-0" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">Education & Background</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Currently pursuing IT Computer Science at Richfield College with a specialization in Data Science. I combine theoretical knowledge with practical application to solve real-world problems through technology and data analysis.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Users className="text-green-400 mt-1 flex-shrink-0" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">Let's Collaborate</h3>
                  <p className="text-slate-300 leading-relaxed mb-4">
                    I'm always eager to collaborate on exciting projects, learn from industry professionals, and contribute to innovative solutions. Looking for:
                  </p>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Data analysis and visualization projects
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Research collaborations
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Mentorship opportunities
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Open-source contributions
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Projects Section */}
        {activeSection === 'projects' && (
          <section className="animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">My Projects</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {projects.map((project, i) => (
                <ProjectCard key={i} {...project} />
              ))}
            </div>
          </section>
        )}

        {/* Skills Section */}
        {activeSection === 'skills' && (
          <section className="animate-fade-in max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Technical Skills</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SkillCategory 
                icon={Database}
                title="Data Science & Analytics"
                items={dataSkills}
              />
              <SkillCategory 
                icon={Code}
                title="Web Development"
                items={webSkills}
              />
              <SkillCategory 
                icon={TrendingUp}
                title="Development Tools"
                items={toolsSkills}
              />
            </div>
          </section>
        )}

        {/* Interests Section */}
        {activeSection === 'interests' && (
          <section className="animate-fade-in max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Areas of Interest</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {interests.map((interest, i) => (
                <InterestCard key={i} {...interest} />
              ))}
            </div>
          </section>
        )}

        {/* Contact Section */}
        {activeSection === 'contact' && (
          <section className="animate-fade-in max-w-2xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">Get In Touch</h2>
            <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-8 border border-purple-500/20">
              <p className="text-slate-300 text-center mb-8 text-lg">
                Feel free to reach out! I'm excited to bring my analytical mindset and technical skills to real-world challenges.
              </p>
              <div className="space-y-4">
                <a href="mailto:skgole6@gmail.com" className="flex items-center justify-center gap-3 p-4 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors">
                  <Mail className="text-purple-400" />
                  <span className="text-white">skgole6@gmail.com</span>
                </a>
                <a href="tel:0677267091" className="flex items-center justify-center gap-3 p-4 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors">
                  <Phone className="text-purple-400" />
                  <span className="text-white">067 726 7091</span>
                </a>
                <a href="https://www.linkedin.com/in/sello-kgole-ba450a295" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 p-4 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors">
                  <Linkedin className="text-purple-400" />
                  <span className="text-white">linkedin.com/in/sello-kgole-ba450a295</span>
                </a>
                <a href="https://github.com/sello-bot" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 p-4 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors">
                  <Github className="text-purple-400" />
                  <span className="text-white">github.com/sello-bot</span>
                </a>
              </div>
            </div>
          </section>
        )}
      </main>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Portfolio;